import { useState, useEffect, useCallback, useMemo } from "react";
import { request } from "@/Api";
import { FilesStore } from "@/common/FilesUploader";
import { SectionTitle } from "@/common/typography";
import { Box, Typography, CircularProgress, Divider } from "@mui/material";

/**
 * Displays all files uploaded to AI threads for a specific project.
 * Uses FilesStore in read-only mode (disabled, no upload/delete).
 */
export default function ProjectThreadFiles({ projectId }) {
    const [threadFiles, setThreadFiles] = useState([]);
    const [loading, setLoading] = useState(false);

    const fetchThreadFiles = useCallback(() => {
        if (!projectId) return;
        setLoading(true);
        request
            .get("/threads/by-model/files", {
                params: { model_type: "project", model_id: projectId },
            })
            .then((res) => setThreadFiles(res.data))
            .catch((err) => console.error("Failed to fetch thread files:", err))
            .finally(() => setLoading(false));
    }, [projectId]);

    useEffect(() => {
        fetchThreadFiles();
    }, [fetchThreadFiles]);

    // Live-sync: refetch when AI assistant uploads new files
    useEffect(() => {
        const handleUpdate = () => fetchThreadFiles();
        window.addEventListener("threadFiles:updated", handleUpdate);
        return () =>
            window.removeEventListener("threadFiles:updated", handleUpdate);
    }, [fetchThreadFiles]);

    // Flatten all files from all threads into a single list for FilesStore
    const allFiles = useMemo(() => {
        return threadFiles.flatMap((threadGroup) =>
            threadGroup.files.map((file) => ({
                id: file.id,
                name: file.name,
                url: file.url,
            })),
        );
    }, [threadFiles]);

    if (loading || allFiles.length === 0) {
        return null;
    }

    return (
        <Box sx={{ mt: 2 }}>
            <Divider sx={{ marginTop: 5, marginBottom: 5 }} />
            <FilesStore
                title="Uploads"
                filesData={allFiles}
                acceptedFormats={[]}
                disabled={true}
            />
        </Box>
    );
}
